/**
 * Typed client for the SFLX Live API.
 *
 * Every call is same-origin in production (the API Worker also serves this
 * bundle) and goes through Vite's `/api` proxy in development, so the session
 * cookie always applies and there is no token juggling in the browser.
 */

import type {
  AccountSummary,
  ActiveViewer,
  ApiErrorBody,
  CategoryDto,
  ContinueWatchingItem,
  CountryBucket,
  ErrorLogEntry,
  FreeCategoryDto,
  FreeChannelDto,
  ImportScope,
  UsaTestPlaylistDto,
  IngestProgress,
  IngestSelection,
  Kind,
  OutputFormat,
  Page,
  PlayRequest,
  PlayResponse,
  ReportErrorInput,
  StreamDto,
} from '@sflx/shared';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiRequestError';
  }

  get code(): string {
    return this.body.error;
  }

  /** Extra guidance the server attached, e.g. how to work around a bare-IP panel. */
  get detail(): string | undefined {
    return this.body.detail;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    // Session cookie. Same-origin in production, same-site in dev.
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const body: ApiErrorBody =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as ApiErrorBody)
        : { error: 'internal', message: `Request failed (${response.status})` };
    throw new ApiRequestError(response.status, body);
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const body = (value: unknown): RequestInit => ({ body: JSON.stringify(value) });

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface SessionUser {
  id: string;
  email: string;
  role: 'admin' | 'viewer';
}

export const auth = {
  me: () => request<{ user: SessionUser }>('/auth/me'),
  requestLink: (email: string) =>
    request<{ delivered: 'email' | 'response'; link?: string; code?: string }>('/auth/request', {
      method: 'POST',
      ...body({ email }),
    }),
  verify: (token: string) => request<{ user: SessionUser }>('/auth/verify', { method: 'POST', ...body({ token }) }),
  verifyCode: (email: string, code: string) =>
    request<{ user: SessionUser }>('/auth/verify', { method: 'POST', ...body({ email, code }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST', ...body({}) }),
};

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export interface CreateAccountBody {
  label?: string;
  serverUrl?: string;
  username?: string;
  password?: string;
  /** Public playlist import. Leave username and password empty. */
  importScope?: ImportScope | null;
  /** The playlist address, sent only with `importScope: 'custom'`. */
  playlistUrl?: string;
  userAgent?: string;
  ingest: IngestSelection;
  liveCountryCodes?: string[] | null;
  includeOther?: boolean;
  outputFormat?: OutputFormat;
}

export const accounts = {
  list: () => request<{ accounts: AccountSummary[] }>('/accounts'),
  get: (id: string) => request<{ account: AccountSummary; jobs: IngestProgress[] }>(`/accounts/${id}`),
  create: (input: CreateAccountBody) =>
    request<{ account: AccountSummary; jobIds: string[] }>('/accounts', { method: 'POST', ...body(input) }),
  update: (id: string, patch: Partial<CreateAccountBody>) =>
    request<{ account: AccountSummary; jobIds: string[] }>(`/accounts/${id}`, { method: 'PATCH', ...body(patch) }),
  remove: (id: string) => request<void>(`/accounts/${id}`, { method: 'DELETE' }),
  refresh: (id: string) => request<{ jobIds: string[] }>(`/accounts/${id}/refresh`, { method: 'POST', ...body({}) }),
  check: (id: string) => request<{ panel: unknown }>(`/accounts/${id}/check`, { method: 'POST', ...body({}) }),
  status: (id: string) => request<{ jobs: IngestProgress[] }>(`/accounts/${id}/status`),
  dismissJob: (id: string, jobId: string) =>
    request<void>(`/accounts/${id}/jobs/${encodeURIComponent(jobId)}/dismiss`, { method: 'POST', ...body({}) }),
  countries: (id: string) => request<{ countries: CountryBucket[] }>(`/accounts/${id}/countries`),
};

export interface Invite {
  id: string;
  email: string;
  invitedBy: string;
  createdAt: number;
  acceptedAt: number | null;
}

export const invites = {
  list: () => request<{ invites: Invite[] }>('/invites'),
  create: (email: string) =>
    request<{ invite: Invite; delivered: 'email' | 'response'; link?: string; code?: string }>('/invites', {
      method: 'POST',
      ...body({ email }),
    }),
  revoke: (id: string) => request<void>(`/invites/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export interface StreamQueryParams {
  kind: Kind;
  category?: string | null;
  countries?: string[] | null;
  other?: boolean;
  q?: string | null;
  sort?: 'name' | 'added' | 'rating';
  favorites?: boolean;
  cursor?: string | null;
  limit?: number;
}

function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      // An EMPTY array is a real selection, not an absent one: `countries=`
      // with `other=1` is how the API is asked for only the unmatched bucket.
      // Dropping it here silently fell back to the account's saved filter.
      search.set(key, value.join(','));
      continue;
    }
    if (typeof value === 'boolean') {
      search.set(key, value ? '1' : '0');
      continue;
    }
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Exported for tests: the absent-vs-empty distinction is easy to regress. */
export const buildQueryForTest = toQuery;

export interface MovieDetailResponse {
  stream: StreamDto | null;
  detail: {
    title: string | null;
    plot: string | null;
    cast: string | null;
    director: string | null;
    genre: string | null;
    releaseDate: string | null;
    rating: number | null;
    durationS: number | null;
    coverUrl: string | null;
    backdropUrl: string | null;
    trailer: string | null;
    container: string | null;
    streamId: number | null;
  };
}

export interface SeriesEpisode {
  id: string;
  season: number;
  episodeNumber: number | null;
  title: string;
  plot: string | null;
  durationS: number | null;
  stillUrl: string | null;
  container: string;
}

export interface SeriesSeason {
  season: number;
  name: string;
  overview: string | null;
  coverUrl: string | null;
  episodes: SeriesEpisode[];
}

export interface SeriesDetailResponse {
  stream: StreamDto | null;
  detail: {
    title: string | null;
    plot: string | null;
    cast: string | null;
    director: string | null;
    genre: string | null;
    releaseDate: string | null;
    rating: number | null;
    coverUrl: string | null;
    backdropUrl: string | null;
    trailer: string | null;
    seasons: SeriesSeason[];
  };
}

export const catalog = {
  categories: (accountId: string, kind: Kind, countries?: string[] | null, other?: boolean, scope?: 'all') =>
    request<{ categories: CategoryDto[] }>(
      `/catalog/${accountId}/categories${toQuery({ kind, countries, other, scope })}`,
    ),

  countries: (accountId: string, kind: Kind = 'live') =>
    request<{ countries: CountryBucket[] }>(`/catalog/${accountId}/countries${toQuery({ kind })}`),

  streams: (accountId: string, params: StreamQueryParams) =>
    request<Page<StreamDto> & { total?: number }>(`/catalog/${accountId}/streams${toQuery({ ...params })}`),

  stream: (accountId: string, kind: Kind, id: number) =>
    request<{ stream: StreamDto }>(`/catalog/${accountId}/streams/${kind}/${id}`),

  movie: (accountId: string, id: number) => request<MovieDetailResponse>(`/catalog/${accountId}/movie/${id}`),

  series: (accountId: string, id: number) => request<SeriesDetailResponse>(`/catalog/${accountId}/series/${id}`),

  /**
   * Pin a category's country when the parser guessed wrong.
   *
   * `code` pins that country. `null` with `pin` releases the category back to
   * the parser; `null` without it pins the category to NO country, which is how
   * a confidently mis-parsed one is moved into the Other bucket.
   */
  setCategoryCountry: (accountId: string, kind: Kind, categoryId: string, code: string | null, pin = true) =>
    request<void>(`/catalog/${accountId}/categories/${kind}/${encodeURIComponent(categoryId)}/country`, {
      method: 'POST',
      ...body({ code, pin }),
    }),

  setCategoryHidden: (accountId: string, kind: Kind, categoryId: string, hidden: boolean) =>
    request<void>(`/catalog/${accountId}/categories/${kind}/${encodeURIComponent(categoryId)}/hidden`, {
      method: 'POST',
      ...body({ hidden }),
    }),
};

// ---------------------------------------------------------------------------
// Playback and user state
// ---------------------------------------------------------------------------

export const play = (input: PlayRequest) => request<PlayResponse>('/play', { method: 'POST', ...body(input) });

// ---------------------------------------------------------------------------
// Free TV
// ---------------------------------------------------------------------------

export const freeTv = {
  categories: () => request<{ categories: FreeCategoryDto[] }>('/free/categories'),

  channels: (params: { category?: string | null; q?: string | null; cursor?: string | null; limit?: number }) =>
    request<Page<FreeChannelDto>>(`/free/channels${toQuery(params)}`),

  play: (channelId: string) => request<PlayResponse>('/free/play', { method: 'POST', ...body({ channelId }) }),

  /**
   * A public playlist. `us` is the iptv-org United States file, `all` is its full index, and
   * `custom` is the address saved on the named account.
   */
  usaPlaylist: (scope: ImportScope, accountId?: string | null) =>
    request<UsaTestPlaylistDto>(
      `/free/playlist?scope=${scope}${accountId ? `&account=${encodeURIComponent(accountId)}` : ''}`,
    ),

  playUsa: (channelId: string, scope: ImportScope, accountId?: string | null) =>
    request<PlayResponse>('/free/playlist/play', {
      method: 'POST',
      ...body({ channelId, scope, ...(accountId ? { accountId } : {}) }),
    }),
};

export const state = {
  favorite: (accountId: string, kind: Kind, xtreamId: number, favorite: boolean) =>
    request<void>('/state/favorite', { method: 'PUT', ...body({ accountId, kind, xtreamId, favorite }) }),

  progress: (input: { accountId: string; kind: Kind; xtreamId: number; episodeId?: string; positionS: number; durationS?: number }) =>
    request<void>('/state/progress', { method: 'PUT', ...body(input) }),

  clearProgress: (input: { accountId: string; kind: Kind; xtreamId: number; episodeId?: string }) =>
    request<void>('/state/progress', { method: 'DELETE', ...body({ ...input, positionS: 0 }) }),

  continueWatching: (limit = 20) => request<{ items: ContinueWatchingItem[] }>(`/state/continue${toQuery({ limit })}`),
};

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

export const presence = {
  heartbeat: (input: { accountId: string; kind: Kind; xtreamId: number; episodeId?: string; title: string }) =>
    request<void>('/presence', { method: 'PUT', ...body(input) }),

  list: () => request<{ viewers: ActiveViewer[] }>('/presence'),

  /** Who else is on this account right now, and how many connections it allows. */
  forAccount: (accountId: string) =>
    request<{ viewers: ActiveViewer[]; maxConnections: number | null }>(`/presence/account/${accountId}`),
};

// ---------------------------------------------------------------------------
// Error log
// ---------------------------------------------------------------------------

export const errorLog = {
  report: (input: ReportErrorInput) => request<void>('/errors/client', { method: 'POST', ...body(input) }),

  list: (limit = 50) => request<{ items: ErrorLogEntry[] }>(`/errors${toQuery({ limit })}`),
};

// ---------------------------------------------------------------------------
// Diagnostics
// ---------------------------------------------------------------------------

/** Loosely typed: the shape differs by which check failed, and the whole point is to show it verbatim. */
export interface StreamDiagnosticsResult {
  ok: boolean;
  hint?: string;
  error?: string | null;
  status?: number;
  streamOrigin?: string;
  [key: string]: unknown;
}

export const diagnostics = {
  stream: () => request<StreamDiagnosticsResult>('/diagnostics/stream'),
};
